import { Tenant, Language, Address } from '@future-harry/nxt-vue-library';
import { ContentBlock } from '@/modules/content-blocks/interfaces';

export interface ApplicationFields {
   cv: boolean | null;
   name: boolean | null;
   email: boolean | null;
   phone: boolean | null;
   photo: boolean | null;
   address: boolean | null;
   motivation: boolean | null;
   date_of_birth: boolean | null;
}

export interface Category {
   id: number;
   sync_id?: string;
   name: Record<string, string>;
   image_url: string | null;
   image_id: number | null;
}

export interface AllowedCompany {
   id: number;
   name: string;
}

export enum JobBoardMetaDataKey {
   Title = 'title',
   Description = 'description',
   About = 'about',
   Keywords = 'keywords',
   OpenGraph = 'open_graph',
}

export enum SocialUrlsDataKey {
   X = 'x',
   Facebook = 'facebook',
   LinkedIn = 'linkedin',
   Instagram = 'instagram',
}

export interface PhoneNumber {
   country_code: string | null;
   e164_format: string | null;
   international_format: string | null;
   national_format: string | null;
   prefix: string | null;
   type: string | null;
}

export type JobBoardMetaData = Partial<Record<JobBoardMetaDataKey, string>>;
export type JobBoardSocialUrls = Partial<Record<SocialUrlsDataKey, string>>;
export interface JobBoardAddress extends Partial<Address> {
   id: number;
   job_board_id: number;
   phone_number: PhoneNumber | null;
}

export interface PageVersion {
   id: number;
   page_id: number;
   author_id: number;
   title: string;
   slug: string;
   status: string;
   content_blocks: ContentBlock[];
   parsed_blocks: ContentBlock[];
   created_at: string;
   updated_at: string;
   deleted_at?: string | null;
   page?: Page;
}

export interface Page {
   id: number;
   job_board_id: number;
   published_version_id: number | null;
   created_at: string;
   updated_at: string;
   latest_version: PageVersion;
}

export interface JobBoardResponse {
   apply_spontaneous_url: string | null;
   border_radius: string | null;
   contact_email: string | null;
   created_at: string;
   favicon_url: string | null;
   font_family: string | null;
   home_url: string | null;
   id: number;
   is_published: boolean;
   logo_image_url: string | null;
   meta_data: JobBoardMetaData | null;
   name: string;
   primary_color: string | null;
   secondary_color: string | null;
   primary_font_color: string | null;
   secondary_font_color: string | null;
   social_urls: JobBoardSocialUrls | null;
   tenant_id: number;
   updated_at: string;
   url: string;
   default_language_id: number | null;
   tenant?: Tenant;
   languages?: Language[];
   pages?: Page[];
   address?: JobBoardAddress;
   cookie_policy: Record<string, string> | null;
   privacy_policy: Record<string, string> | null;
   terms_of_service: Record<string, string> | null;
   categories?: Category[];
   customers?: AllowedCompany[];
   application_fields: ApplicationFields;
}

export interface JobBoardAddressInternal extends Omit<JobBoardAddress, 'phone_number'> {
   phone_number: string | null;
}

export interface JobBoard
   extends Omit<
      JobBoardResponse,
      | 'languages'
      | 'default_language_id'
      | 'meta_data'
      | 'social_urls'
      | 'font_family'
      | 'primary_color'
      | 'secondary_color'
      | 'primary_font_color'
      | 'secondary_font_color'
      | 'favicon_url'
      | 'logo_image_url'
      | 'address'
      | 'cookie_policy'
      | 'privacy_policy'
      | 'terms_of_service'
   > {
   meta_data: JobBoardMetaData;
   social_urls: JobBoardSocialUrls;
   languages: Language[];
   language_ids: number[];
   default_language_id: number;
   font_family: string;
   primary_color: string;
   secondary_color: string;
   primary_font_color: string;
   secondary_font_color: string;
   favicon_url: string;
   logo_image_url: string;
   address: Partial<JobBoardAddressInternal>;
   cookie_policy: Record<string, string>;
   privacy_policy: Record<string, string>;
   terms_of_service: Record<string, string>;
   categories: Category[];
   customers: AllowedCompany[];
   application_fields: ApplicationFields;
}
